import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  CompassIcon, Activity, Fingerprint, Check, AlertTriangle, Crown, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";

const BUSINESS_TIER_PLANS = ["professional", "enterprise"];

/**
 * 3-Step Plan / Execute / Track wizard drawer.
 * - PLAN: confirms the project has commercial guardrails (contract_value + target_margin) and shows the scaffolded phases.
 *   If the project was *not* SOW-ingested (no contract_value), an inline brief-ingestion form posts to /api/workflow/plan/ingest-brief.
 * - EXECUTE: quick activity log — task picker + hours. Calls /api/workflow/execute/log-activity, surfaces 202 margin alerts inline.
 * - TRACK: cryptographic milestone sign-off — gated behind the Business tier (Free/Starter sees an upgrade card).
 */
const WorkflowWizard = ({ projectId, projectName, project }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [tasks, setTasks] = useState([]);
  const [activity, setActivity] = useState({ task_id: "", hours_logged: "", activity_notes: "" });
  const [activityResult, setActivityResult] = useState(null);
  const [activityBusy, setActivityBusy] = useState(false);
  const [signOff, setSignOff] = useState({ change_description: "" });
  const [signOffBusy, setSignOffBusy] = useState(false);
  const [signOffResult, setSignOffResult] = useState(null);
  const [brief, setBrief] = useState({ contract_value: "", target_margin_percentage: 30, phases_text: "" });
  const [briefBusy, setBriefBusy] = useState(false);

  const planConfigured = Boolean(project?.contract_value && project?.target_margin_percentage);
  const isBusinessTier = BUSINESS_TIER_PLANS.includes((user?.plan || "free").toLowerCase());

  useEffect(() => {
    if (!open) return;
    api.get(`/tasks?project_id=${projectId}`)
      .then((r) => {
        setTasks(r.data);
        if (r.data.length && !activity.task_id) {
          setActivity((a) => ({ ...a, task_id: r.data[0].task_id }));
        }
      })
      .catch(() => setTasks([]));
  }, [open, projectId]);

  const submitBrief = async () => {
    const cv = parseFloat(brief.contract_value);
    const tm = parseFloat(brief.target_margin_percentage);
    const phases = brief.phases_text.split("\n").map((s) => s.trim()).filter(Boolean);
    if (!(cv >= 0)) return toast.error("Contract value must be ≥ 0");
    if (!(tm >= 0 && tm <= 100)) return toast.error("Target margin must be 0-100");
    if (!phases.length) return toast.error("Add at least one phase");
    setBriefBusy(true);
    try {
      await api.post(`/workflow/plan/ingest-brief`, {
        project_name: projectName,
        contract_value: cv,
        target_margin_percentage: tm,
        extracted_phases: phases,
      });
      toast.success("Brief ingested — financial baselines set");
      // The plan/ingest-brief endpoint creates a NEW project so jump to step 2 once we reload.
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Plan ingestion failed");
    } finally { setBriefBusy(false); }
  };

  const logActivity = async () => {
    if (!activity.task_id) return toast.error("Pick a task");
    const hrs = parseFloat(activity.hours_logged);
    if (!(hrs > 0)) return toast.error("Hours must be > 0");
    setActivityBusy(true);
    setActivityResult(null);
    try {
      const { data, status } = await api.post(`/workflow/execute/log-activity`, {
        task_id: activity.task_id,
        hours_logged: hrs,
        activity_notes: activity.activity_notes,
      });
      // 202 returns {warning, details}; 200 returns burndown directly
      const isAlert = status === 202;
      const payload = isAlert ? data.details : data;
      setActivityResult({ isAlert, payload, warning: isAlert ? data.warning : null });
      if (isAlert) {
        toast.warning(payload.alert_message, { duration: 6000 });
      } else {
        toast.success(`Logged ${hrs}h · margin ${payload.current_margin?.toFixed(1)}%`);
      }
      setActivity((a) => ({ ...a, hours_logged: "", activity_notes: "" }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Log activity failed");
    } finally { setActivityBusy(false); }
  };

  const executeSignOff = async () => {
    if (!signOff.change_description.trim()) return toast.error("Describe the change being signed off");
    setSignOffBusy(true);
    setSignOffResult(null);
    try {
      const { data } = await api.post(`/workflow/track/milestone-signoff`, {
        project_id: projectId,
        change_description: signOff.change_description,
      });
      setSignOffResult(data);
      toast.success("Cryptographic compliance lock applied");
      setSignOff({ change_description: "" });
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Business tier required for milestone sign-off");
      } else {
        toast.error(e.response?.data?.detail || "Sign-off failed");
      }
    } finally { setSignOffBusy(false); }
  };

  const steps = [
    { n: 1, label: "Plan", icon: CompassIcon, complete: planConfigured },
    { n: 2, label: "Execute", icon: Activity, complete: activityResult !== null },
    { n: 3, label: "Track", icon: Fingerprint, complete: signOffResult !== null },
  ];

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="open-workflow-wizard-btn">
          <CompassIcon className="w-4 h-4 mr-1" /> Workflow
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="workflow-wizard-drawer">
        <SheetHeader>
          <SheetTitle className="font-display tracking-tight">Plan · Execute · Track</SheetTitle>
          <SheetDescription className="text-xs">
            PMP-aligned three-step ribbon for <strong>{projectName}</strong>. Move through Plan to Track to ship with full financial and compliance guardrails.
          </SheetDescription>
        </SheetHeader>

        {/* Step indicator */}
        <div className="mt-6 flex items-center gap-1 border-b border-[#1F2937] pb-3">
          {steps.map((s, i) => {
            const Icon = s.icon;
            const isActive = step === s.n;
            return (
              <button
                key={s.n}
                onClick={() => setStep(s.n)}
                className={`flex-1 px-2 py-2 rounded-none border text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-1.5 transition ${
                  isActive
                    ? "bg-[#002FA7] text-white border-[#002FA7] font-semibold"
                    : s.complete
                      ? "bg-emerald-50 text-emerald-700 border-emerald-400"
                      : "bg-[#0D1117] text-slate-300 border-[#374151] hover:border-gray-900"
                }`}
                data-testid={`wizard-step-${s.n}`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span>{s.n}. {s.label}</span>
                {s.complete && !isActive && <Check className="w-3 h-3 ml-1" />}
              </button>
            );
          })}
        </div>

        {/* STEP 1 · PLAN */}
        {step === 1 && (
          <div className="mt-5 space-y-4" data-testid="wizard-step-1-content">
            {planConfigured ? (
              <div className="border border-emerald-400 bg-emerald-50 p-4">
                <div className="flex items-center gap-2 text-emerald-800 text-sm font-semibold">
                  <Check className="w-4 h-4" /> Financial baselines configured
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-xs font-mono text-emerald-900">
                  <div>Contract: <strong>£{Number(project.contract_value).toLocaleString()}</strong></div>
                  <div>Target margin: <strong>{project.target_margin_percentage}%</strong></div>
                </div>
                <div className="mt-3 text-xs text-emerald-900">
                  This project is fully scoped. Move to <button onClick={() => setStep(2)} className="underline font-semibold">Step 2 · Execute</button> to start logging activity.
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="border border-amber-400 bg-amber-50 p-3 text-xs text-amber-900">
                  <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                  This project has no commercial guardrails yet. Provide the contract value and target margin so the GovernanceEngine can monitor margin burn.
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Contract value (£)</Label>
                    <Input type="number" min="0" className="rounded-none mt-1" data-testid="wizard-contract-input" value={brief.contract_value} onChange={(e) => setBrief({ ...brief, contract_value: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Target margin %</Label>
                    <Input type="number" min="0" max="100" className="rounded-none mt-1" data-testid="wizard-margin-input" value={brief.target_margin_percentage} onChange={(e) => setBrief({ ...brief, target_margin_percentage: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.2em]">Delivery phases (one per line)</Label>
                  <Textarea rows={5} className="rounded-none mt-1 font-mono text-xs" placeholder={"Discovery\nBuild\nUAT\nGo-Live"} data-testid="wizard-phases-input" value={brief.phases_text} onChange={(e) => setBrief({ ...brief, phases_text: e.target.value })} />
                </div>
                <div className="text-[10px] text-gray-500">
                  Note: this creates a <em>new</em> project with the scaffolded phases. Use the SOW Ingest from Workspace to bind to this existing project.
                </div>
                <Button onClick={submitBrief} disabled={briefBusy} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="wizard-submit-brief">
                  {briefBusy ? "Ingesting…" : "Ingest brief & scaffold"} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* STEP 2 · EXECUTE */}
        {step === 2 && (
          <div className="mt-5 space-y-4" data-testid="wizard-step-2-content">
            <div className="text-xs text-gray-500">
              Log time against a task. The GovernanceEngine re-evaluates project margin in real time and fires a 202 alert if your cost burn crosses the target.
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em]">Task</Label>
              <Select value={activity.task_id} onValueChange={(v) => setActivity({ ...activity, task_id: v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="wizard-task-select"><SelectValue placeholder="Pick a task" /></SelectTrigger>
                <SelectContent>
                  {tasks.map((t) => <SelectItem key={t.task_id} value={t.task_id}>{t.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em]">Hours</Label>
                <Input type="number" min="0.25" step="0.25" className="rounded-none mt-1" data-testid="wizard-hours-input" value={activity.hours_logged} onChange={(e) => setActivity({ ...activity, hours_logged: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em]">Activity notes</Label>
              <Textarea rows={2} className="rounded-none mt-1" data-testid="wizard-notes-input" value={activity.activity_notes} onChange={(e) => setActivity({ ...activity, activity_notes: e.target.value })} />
            </div>
            <Button onClick={logActivity} disabled={activityBusy} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="wizard-log-activity">
              {activityBusy ? "Logging…" : "Log activity"}
            </Button>

            {activityResult && (
              <div
                className={`border p-3 ${activityResult.isAlert ? "border-rose-400 bg-rose-50" : "border-emerald-400 bg-emerald-50"}`}
                data-testid="wizard-activity-result"
              >
                <div className={`text-xs uppercase tracking-[0.2em] font-semibold ${activityResult.isAlert ? "text-rose-800" : "text-emerald-800"}`}>
                  {activityResult.isAlert ? "Margin Fade Alert (202)" : "Margins healthy (200)"}
                </div>
                <div className="mt-1 text-sm">{activityResult.payload.alert_message}</div>
                <div className={`mt-2 font-mono text-[11px] ${activityResult.isAlert ? "text-rose-700" : "text-emerald-700"}`}>
                  Margin: <strong>{activityResult.payload.current_margin?.toFixed(2)}%</strong> · target {activityResult.payload.target_margin_percentage}% · cost £{Number(activityResult.payload.total_cost_incurred).toLocaleString()}
                </div>
              </div>
            )}

            <div className="text-xs text-gray-500">
              When margins are stable, advance to <button onClick={() => setStep(3)} className="underline font-semibold">Step 3 · Track</button> to commit milestone sign-offs.
            </div>
          </div>
        )}

        {/* STEP 3 · TRACK */}
        {step === 3 && (
          <div className="mt-5 space-y-4" data-testid="wizard-step-3-content">
            {isBusinessTier ? (
              <>
                <div className="text-xs text-gray-500">
                  Lock a milestone or change request with a cryptographic SHA-256 signature. The ledger entry is immutable and proves the approval chain to auditors.
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.2em]">Change being approved</Label>
                  <Textarea
                    rows={4}
                    className="rounded-none mt-1"
                    placeholder="e.g. Sign-off Phase 1 deliverables — UAT passed by client steering committee on 12 June 2026."
                    data-testid="wizard-signoff-description"
                    value={signOff.change_description}
                    onChange={(e) => setSignOff({ ...signOff, change_description: e.target.value })}
                  />
                </div>
                <Button onClick={executeSignOff} disabled={signOffBusy} className="w-full rounded-none bg-emerald-600 hover:bg-emerald-700" data-testid="wizard-execute-signoff">
                  {signOffBusy ? "Signing…" : "Execute cryptographic sign-off"}
                </Button>

                {signOffResult && (
                  <div className="border border-emerald-400 bg-emerald-50 p-3" data-testid="wizard-signoff-result">
                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-800 flex items-center gap-1">
                      <Fingerprint className="w-3 h-3" /> Locked
                    </div>
                    <div className="mt-1 text-sm">{signOffResult.message}</div>
                    <div className="mt-2 font-mono text-[10px] text-slate-300 break-all select-all bg-[#0D1117] border border-emerald-200 px-2 py-1" data-testid="wizard-audit-hash">
                      {signOffResult.audit_hash}
                    </div>
                    <div className="mt-2 text-[10px] text-gray-500">{signOffResult.timestamp}</div>
                  </div>
                )}
              </>
            ) : (
              <div className="border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-yellow-50 p-5 space-y-3" data-testid="wizard-upsell-panel">
                <div className="flex items-center gap-2">
                  <Crown className="w-6 h-6 text-amber-600" />
                  <div className="font-display font-bold text-lg tracking-tight">Business tier required</div>
                </div>
                <div className="text-sm text-gray-600">
                  Cryptographic milestone sign-off is a Business-tier feature. The SHA-256 audit ledger gives you a tamper-evident chain of every scope change — essential for enterprise clients and regulated industries.
                </div>
                <div className="bg-[#0D1117] border border-amber-200 p-3 text-xs space-y-1">
                  <div className="flex items-center gap-1 text-emerald-700"><Check className="w-3 h-3" /> Immutable SHA-256 audit ledger</div>
                  <div className="flex items-center gap-1 text-emerald-700"><Check className="w-3 h-3" /> Tamper-evident milestone history</div>
                  <div className="flex items-center gap-1 text-emerald-700"><Check className="w-3 h-3" /> Defensible compliance posture</div>
                </div>
                <Link to="/billing">
                  <Button className="w-full rounded-none bg-amber-600 hover:bg-amber-700" data-testid="wizard-upgrade-cta">
                    Upgrade to Business <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default WorkflowWizard;
